import { Box, IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import { useL10n } from '@uplift/hooks';
import React, { useMemo } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Tooltip } from '@uplift/ui-v2';
import uniqBy from 'lodash/uniqBy';
import { ProviderFinancePeriodItem, ProviderFinancePeriodItemType } from '@uplift/api';

export type AppointmentDetailsCellProps = {
  row: ProviderFinancePeriodItem;
  displayInline?: boolean;
};

export const useStyles = makeStyles()({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  strikethrough: {
    textDecoration: 'line-through'
  }
});

export const AppointmentDetailsCell: React.FC<AppointmentDetailsCellProps> = ({ row }) => {
  const { classes, cx } = useStyles();
  const l10n = useL10n();
  const isRefunded = useMemo(() => {
    if (row.payment && row.payment.refundedAt) {
      return true;
    }
    return false;
  }, [ row.payment ]);

  const containerClassName = cx(classes.root);

  if (row instanceof Object) {
    if (row.itemType === ProviderFinancePeriodItemType.cancelledSession) {
      return (
        <Box className={containerClassName}>
          <Tooltip
            arrow
            title={(
              <Typography style={{ fontWeight: 400 }} variant="body2" color="inherit">
                {isRefunded ? l10n.finance.monthly.removedAndIssuedRefund : l10n.finance.monthly.canceledWithLateFee}
              </Typography>
            )}
          >
            <IconButton>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" color="textPrimary" className={isRefunded ? classes.strikethrough : ''}>Late fee</Typography>
        </Box>
      );
    }

    if (row.cptCodes) {
      const uniqCodes = uniqBy(row.cptCodes, 'code');
      const codesString = row.cptCodes.map(c => c.code).join(', ');

      return (
        <Box className={containerClassName}>
          <Tooltip
            arrow
            title={(
              <Typography style={{ fontWeight: 400 }} variant="body2" color="inherit">
                {uniqCodes.length === 1
                  ? (
                    <Box>{uniqCodes[0].name}</Box>
                  ) : (
                    uniqCodes.map((code, index) =>
                      <Box key={index}><strong>{code.code}</strong>: {code.name}</Box>
                    )
                  )}
              </Typography>
            )}
          >
            <IconButton>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" color="textPrimary">{codesString}</Typography>
        </Box>
      );
    }
  }

  return null;
};


export const AppointmentDetailsInlineCell: React.FC<AppointmentDetailsCellProps> = ({ row }) => {
  const { classes, cx } = useStyles();
  const l10n = useL10n();

  const containerClassName = cx(classes.root);

  if (row instanceof Object) {
    if (row.itemType === ProviderFinancePeriodItemType.cancelledSession) {
      return (
        <Box className={containerClassName}>
          <Typography variant="body2" color="textPrimary">{l10n.finance.monthly.lateFee}</Typography>
          <span>&nbsp;—&nbsp;</span>
          <Typography variant="body2" color="textPrimary">{l10n.finance.monthly.cancelled}</Typography>
        </Box>
      );
    }

    if (row.cptCodes) {
      return (
        <Box className={containerClassName}>
          <Box>
            <table>
              <tbody>
                {row.cptCodes.map((code, index) => (
                  <tr key={index}>
                    <td style={{ verticalAlign: 'top', paddingRight: '4px' }}>
                      <Typography variant="body2" color="textPrimary">{code.code}</Typography>
                    </td>
                    <td style={{ paddingLeft: '4px' }}>
                      <Typography variant="body2" color="textPrimary" style={{ whiteSpace: 'pre-wrap' }}>{code.name.trim()}</Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      );
    }
  }

  return null;
};
